import { IError, IQueryHookOptions } from '@comptia-sso/core';
import { useQuery } from 'react-query';

// Services.
import { getCoppaInstructors } from 'services';

import { IInstructor } from 'types';

type UseCoppaInstructors = [IInstructor[], IQueryHookOptions];

const parseInstructors = (raw: string): IInstructor[] => {
  const value = (raw || '').trim();
  if (!value) return [];

  try {
    let parsed: unknown = JSON.parse(value);

    // The value may arrive as a JSON-encoded string (JSON inside JSON), in
    // which case parsing once yields a string; parse again to reach the array.
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }

    if (Array.isArray(parsed)) {
      return parsed
        .map((item): IInstructor => {
          const record = (item ?? {}) as Record<string, unknown>;
          return {
            email: typeof record.email === 'string' ? record.email.trim() : '',
            firstName:
              typeof record.firstName === 'string'
                ? record.firstName.trim()
                : '',
            lastName:
              typeof record.lastName === 'string' ? record.lastName.trim() : '',
          };
        })
        .filter((instructor) => instructor.email.length > 0);
    }
  } catch {
    // Not JSON; ignore and return an empty list.
  }

  return [];
};

export const useCoppaInstructors = (
  id = '',
  enabled = true,
): UseCoppaInstructors => {
  const { data, error, isError, isLoading } = useQuery<IInstructor[], IError>(
    ['coppa-instructors', id],
    async () => {
      const response = await getCoppaInstructors(id);
      return parseInstructors(response?.instructors);
    },
    {
      enabled: !!id && enabled,
    },
  );

  return [data ?? [], { error, isError, isLoading }];
};
